import React from 'react';
import PropTypes from 'prop-types';
import { format, subDays } from 'date-fns';
import { de } from 'date-fns/locale';

const ActivityHeatmap = ({ activityData = {} }) => {
    const getIntensityClass = (activity) => {
        if (activity === 0) return 'bg-base-300';
        if (activity <= 10) return 'bg-primary/10';
        if (activity <= 20) return 'bg-primary/20';
        if (activity <= 50) return 'bg-primary/40';
        if (activity <= 80) return 'bg-primary/60';
        if (activity <= 100) return 'bg-primary/80';
        return 'bg-primary';
    };

    const renderGrid = () => {
        const days = [];
        const today = new Date();
        
        // Aktivitätsdaten der letzten 14 Tage
        for (let i = 13; i >= 0; i--) {
            const date = subDays(today, i);
            const dateStr = format(date, 'yyyy-MM-dd');
            const activity = activityData[dateStr] || 0;
            const isToday = i === 0;
            const dayName = format(date, 'EEEEE', { locale: de });

            days.push({
                date,
                dateStr,
                activity,
                isToday,
                dayName
            });
        }

        // Teile die Tage in zwei Wochen auf
        const thisWeek = days.slice(-7);
        const lastWeek = days.slice(0, 7);

        // Definiere die Wochentage in der richtigen Reihenfolge
        const weekdays = ['M', 'D', 'M', 'D', 'F', 'S', 'S'];

        return (
            <div className="card bg-base-200 shadow-lg h-full">
                <div className="card-body p-4">
                    <div className="space-y-3">
                        {/* Header */}
                        <div className="flex items-center justify-between">
                            <h3 className="font-medium text-sm">Aktivität</h3>
                            <div className="flex items-center gap-2">
                                <span className="text-xs text-base-content/70">0</span>
                                <div className="flex items-center gap-0.5">
                                    <div className="w-2 h-2 rounded-full bg-base-300" />
                                    <div className="w-2 h-2 rounded-full bg-primary/10" />
                                    <div className="w-2 h-2 rounded-full bg-primary/40" />
                                    <div className="w-2 h-2 rounded-full bg-primary/80" />
                                    <div className="w-2 h-2 rounded-full bg-primary" />
                                </div>
                                <span className="text-xs text-base-content/70">100+</span>
                            </div>
                        </div>

                        {/* Grid Container */}
                        <div className="grid grid-cols-[auto_1fr] gap-3">
                            {/* Leere Zelle für Ausrichtung mit Label */}
                            <div className="w-16" />
                            
                            {/* Wochentage Grid */}
                            <div className="grid grid-cols-7 gap-1.5">
                                {weekdays.map((day, index) => (
                                    <div key={index} className="text-[11px] text-base-content/50 font-medium text-center">
                                        {day}
                                    </div>
                                ))}
                            </div>

                            {/* Letzte Woche */}
                            <div className="text-[11px] text-base-content/50 w-16 text-right">
                                Letzte Woche
                            </div>
                            <div className="grid grid-cols-7 gap-1.5">
                                {lastWeek.map((day) => (
                                    <div key={day.dateStr} className="relative group">
                                        <div 
                                            className={`
                                                relative
                                                w-full aspect-square rounded-md
                                                ${getIntensityClass(day.activity)}
                                                ${day.isToday ? 'ring-1 ring-primary' : ''}
                                                transition-all duration-200
                                                hover:scale-105
                                                cursor-pointer
                                                group
                                            `}
                                        >
                                            <div className="
                                                invisible group-hover:visible
                                                absolute left-1/2 top-0 -translate-y-full -translate-x-1/2
                                                bg-base-200 text-base-content
                                                px-2 py-1.5 rounded-lg
                                                text-xs
                                                whitespace-nowrap
                                                ring-1 ring-base-content/10
                                                pointer-events-none
                                                mb-2
                                                z-50
                                            ">
                                                {day.activity} Fragen am {format(day.date, 'dd.MM.', { locale: de })}
                                                <div className="
                                                    absolute -bottom-1 left-1/2 -translate-x-1/2
                                                    border-4 border-transparent
                                                    border-t-base-200
                                                    pointer-events-none
                                                "/>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Diese Woche */}
                            <div className="text-[11px] text-base-content/50 w-16 text-right">
                                Diese Woche
                            </div>
                            <div className="grid grid-cols-7 gap-1.5">
                                {thisWeek.map((day) => (
                                    <div key={day.dateStr} className="relative group">
                                        <div 
                                            className={`
                                                relative
                                                w-full aspect-square rounded-md
                                                ${getIntensityClass(day.activity)}
                                                ${day.isToday ? 'ring-1 ring-primary' : ''}
                                                transition-all duration-200
                                                hover:scale-105
                                                cursor-pointer
                                                group
                                            `}
                                        >
                                            <div className="
                                                invisible group-hover:visible
                                                absolute left-1/2 top-0 -translate-y-full -translate-x-1/2
                                                bg-base-200 text-base-content
                                                px-2 py-1.5 rounded-lg
                                                text-xs
                                                whitespace-nowrap
                                                ring-1 ring-base-content/10
                                                pointer-events-none
                                                mb-2
                                                z-50
                                            ">
                                                {day.activity} Fragen am {format(day.date, 'dd.MM.', { locale: de })}
                                                <div className="
                                                    absolute -bottom-1 left-1/2 -translate-x-1/2
                                                    border-4 border-transparent
                                                    border-t-base-200
                                                    pointer-events-none
                                                "/>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        renderGrid()
    );
};

ActivityHeatmap.propTypes = {
    activityData: PropTypes.objectOf(PropTypes.number)
};

export default ActivityHeatmap; 